import { useId, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { AssignmentDefaults, AssignmentPlayer, CreateAssignmentInput, NotifyTarget, PlayerGroup } from '@workflow/contracts'
import { Button, Checkbox, ConfirmButton, Dialog, Dropdown, FADE, Field, IconButton, Icons, Segmented, cn, TextArea, TextField } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useAssignmentPlayers, useDeletePlayerGroup, usePlayerGroups, useSavePlayerGroup, useSession } from '@/lib/api/hooks'

const fields = ['paymentAmount', 'estimatedHours', 'notes'] as const
type ValueKey = typeof fields[number]
type Values = Partial<Record<ValueKey, string>>
export type AssignmentDraft = { defaults: Values; players: Record<string, Values> }

const labels: Record<ValueKey, string> = { paymentAmount: 'Payment Rate', estimatedHours: 'Hours', notes: 'Notes' }
const names = (player: AssignmentPlayer) => player.discordName || player.email
const blank = (): AssignmentDraft => ({ defaults: {}, players: {} })

export function newAssignmentDraft(rate: string): AssignmentDraft {
  return { ...blank(), defaults: rate ? { paymentAmount: rate } : {} }
}

function read(values: Values, clear: boolean): AssignmentDefaults {
  const { paymentAmount, estimatedHours, notes } = values
  return {
    ...(paymentAmount?.trim() ? { paymentAmount: Math.round(Number(paymentAmount) * 100) } : clear && paymentAmount !== undefined ? { paymentAmount: null } : {}),
    ...(estimatedHours?.trim() ? { estimatedHours: Number(estimatedHours) } : clear && estimatedHours !== undefined ? { estimatedHours: null } : {}),
    ...(notes?.trim() ? { notes: notes.trim() } : clear && notes !== undefined ? { notes: null } : {}),
  }
}

export function resolveAssignments(draft: AssignmentDraft): CreateAssignmentInput[] {
  const defaults = read(draft.defaults, true)
  return Object.entries(draft.players).map(([playerId, values]) => ({ playerId, ...defaults, ...read(values, true) }))
}

function valid(values: Values) {
  return fields.every((key) => {
    const value = values[key]?.trim()
    if (!value) return true
    if (key === 'notes') return value.length <= 2000
    const number = Number(value)
    return /^\d*\.?\d+$/.test(value) && number >= 0 && number <= (key === 'paymentAmount' ? 1_000_000 : 10_000)
  })
}

export function AssignmentPanel({ initial, blocked = [], draftJob, staged, currency, busy, error, onClose, onApply }: {
  initial: AssignmentDraft
  blocked?: readonly string[]
  draftJob: boolean
  staged?: boolean
  currency: string
  busy?: boolean
  error?: unknown
  onClose: () => void
  onApply: (draft: AssignmentDraft, notify: NotifyTarget) => void
}) {
  const label = useId()
  const roster = useAssignmentPlayers(true)
  const groups = usePlayerGroups(true)
  const isAdmin = useSession().data?.account?.role === 'admin'
  const [draft, setDraft] = useState(initial)
  const [groupId, setGroupId] = useState('')
  const [managingGroups, setManagingGroups] = useState(false)
  const [notify, setNotify] = useState<NotifyTarget>('assigned')
  const [skipped, setSkipped] = useState(0)
  const players = roster.data ?? []
  const options = players.filter(({ id }) => !blocked.includes(id))
  const selected = Object.keys(draft.players)
  const selectedAvailable = selected.every((id) => options.some((player) => player.id === id))
  const validDraft = valid(draft.defaults) && Object.values(draft.players).every(valid)
  const group = groups.data?.find(({ id }) => id === groupId)

  const loadGroup = (id: string) => {
    const saved = groups.data?.find((entry) => entry.id === id)
    if (!saved) return
    const eligible = saved.players.filter(({ playerId }) => options.some((player) => player.id === playerId))
    setSkipped(saved.players.length - eligible.length)
    setGroupId(id)
    setDraft((current) => ({ ...current, players: Object.fromEntries(eligible.map(({ playerId }) => [playerId, current.players[playerId] ?? {}])) }))
  }

  return (
    <Dialog open onClose={busy ? () => {} : managingGroups ? () => setManagingGroups(false) : onClose} labelledBy={label} className="max-w-2xl rounded-2xl border bg-surface shadow-modal">
      <div className="max-h-[calc(100dvh-2rem)] overflow-y-auto px-5 pt-5 sm:max-h-[calc(100dvh-4rem)] sm:px-7 sm:pt-6">
        <AnimatePresence mode="wait" initial={false}>
        {managingGroups ? (
          <motion.div key="groups" {...FADE}>
            <GroupManager label={label} groups={groups.data ?? []} loading={groups.isLoading} error={groups.error} players={players}
              onClose={() => setManagingGroups(false)} onDelete={(id) => {
                if (groupId === id) {
                  setGroupId('')
                  setSkipped(0)
                }
              }} />
          </motion.div>
        ) : (
          <motion.div key="assignment" {...FADE}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 id={label} className="font-display text-2xl text-foreground">Assign Players</h2>
                <p className="mt-1 text-caption text-muted-foreground">Pick players or start with a saved group.</p>
              </div>
              <IconButton size="sm" label="Close assignment panel" disabled={busy} onClick={onClose}><Icons.close /></IconButton>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Dropdown ariaLabel="Saved player group" placeholder="Choose Group" emptyLabel={groups.isLoading ? 'Loading Groups…' : 'No Saved Groups Yet'} value={groupId}
                icon={<Icons.members size="sm" />}
                className={cn('min-w-36 flex-1 [&>button>span>span[aria-hidden]]:hidden sm:[&>button>span>span[aria-hidden]]:inline-flex sm:max-w-72 [&>button]:rounded-full [&>button]:bg-tint [&>button]:px-3.5',
                  group && '[&>button]:border-foreground/30')}
                options={(groups.data ?? []).map((entry) => ({ value: entry.id, label: entry.name, description: `${entry.players.length} players` }))}
                onChange={loadGroup} disabled={!roster.data || busy} />
              {isAdmin ? <Button size="sm" variant="primary" icon={<Icons.members />} onClick={() => setManagingGroups(true)} disabled={busy}>Groups</Button> : null}
            </div>
            {group ? <p className="mt-1.5 text-caption text-muted-foreground">Switching groups replaces the player selection. Shared defaults stay with this job.</p> : null}
            {skipped > 0 ? <p className="mt-1 text-caption text-muted-foreground">{skipped} group members are inactive or already assigned.</p> : null}
            {groups.error || roster.error ? <p role="alert" className="mt-2 text-caption text-danger">{describeError(roster.error ?? groups.error)}</p> : null}
            <fieldset disabled={busy} className="mt-5 min-w-0">
              <AssignmentFields draft={draft} onChange={setDraft} players={options} currency={currency} />
              {!draftJob ? (
                <div className="mt-4">
                  <Segmented<NotifyTarget> label="Notification audience" value={notify} onChange={setNotify}
                    options={[{ value: 'assigned', label: 'Assigned players' }, { value: 'everyone', label: 'Every player' }, { value: 'none', label: 'No one' }]} />
                </div>
              ) : <p className="mt-3 text-caption text-muted-foreground">Players are notified when the draft is published.</p>}
            </fieldset>
            {selected.length > 50 ? <p role="alert" className="mt-2 text-caption text-danger">Choose up to 50 players.</p> : null}
            {!validDraft ? <p role="alert" className="mt-2 text-caption text-danger">Use a rate from 0 to 1,000,000 and hours from 0 to 10,000.</p> : null}
            {error ? <p role="alert" className="mt-2 text-caption text-danger">{describeError(error)}</p> : null}
            <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-hairline bg-surface py-3">
              <span aria-live="polite" className={cn('mr-auto text-caption text-muted-foreground tabular-nums', !staged && 'sr-only sm:not-sr-only sm:mr-auto')}>{selected.length} selected</span>
              <Button size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button size="sm" variant="primary" busy={busy} disabled={(!staged && !selected.length) || selected.length > 50 || !validDraft || !selectedAvailable}
                onClick={() => onApply(draft, draftJob ? 'none' : notify)}>
                {staged ? 'Use selection' : selected.length ? `Assign ${selected.length} ${selected.length === 1 ? 'player' : 'players'}` : 'Assign players'}
              </Button>
            </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>
    </Dialog>
  )
}

function GroupManager({ label, groups, loading, error, players, onClose, onDelete }: {
  label: string; groups: PlayerGroup[]; loading: boolean; error: unknown; players: AssignmentPlayer[]
  onClose: () => void; onDelete: (id: string) => void
}) {
  const [editor, setEditor] = useState<'new' | PlayerGroup | null>(null)
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 id={label} className="font-display text-2xl text-foreground">Groups</h2>
        <IconButton size="sm" label="Back to assignment" onClick={onClose}><Icons.back /></IconButton>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        {editor ? (
          <motion.div key={editor === 'new' ? 'new' : editor.id} {...FADE}>
            <GroupEditor group={editor === 'new' ? null : editor} players={players} onClose={() => setEditor(null)}
              onDelete={(id) => { onDelete(id); setEditor(null) }} />
          </motion.div>
        ) : (
          <motion.div key="list" {...FADE} className="space-y-4 pb-5 pt-2">
            <p className="text-caption text-muted-foreground">Save a roster to reuse across jobs.</p>
            {loading ? <p className="text-caption text-muted-foreground">Loading Groups…</p>
              : error ? <p role="alert" className="text-caption text-danger">{describeError(error)}</p>
              : groups.length ? (
                <ul className="max-h-64 space-y-1 overflow-auto">
                  {groups.map((group) => (
                    <li key={group.id}>
                      <button type="button" onClick={() => setEditor(group)}
                        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-fast hover:bg-tint focus-visible:outline-primary">
                        <span className="min-w-0 flex-1 truncate text-ui font-medium">{group.name}</span>
                        <span className="text-caption text-muted-foreground">{group.players.length} players</span>
                        <Icons.edit size="sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <p className="py-4 text-caption text-muted-foreground">No saved groups yet.</p>}
            <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setEditor('new')}>New Group</Button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

function GroupEditor({ group, players, onClose, onDelete }: {
  group: PlayerGroup | null; players: AssignmentPlayer[]; onClose: () => void; onDelete: (id: string) => void
}) {
  const nameId = useId()
  const [name, setName] = useState(group?.name ?? '')
  const [draft, setDraft] = useState<AssignmentDraft>(() => ({
    defaults: {},
    players: Object.fromEntries((group?.players ?? []).map(({ playerId }) => [playerId, {}])),
  }))
  const save = useSavePlayerGroup()
  const remove = useDeletePlayerGroup()
  const busy = save.isPending || remove.isPending
  const ids = Object.keys(draft.players)
  const canSave = name.trim() && ids.length >= 2 && ids.length <= 50 && ids.every((id) => players.some((player) => player.id === id))
  return (
    <>
      <h3 className="mt-2 text-ui font-medium">{group ? 'Edit Group' : 'New Group'}</h3>
      <p className="mt-1 text-caption text-muted-foreground">Select 2–50 active players.</p>
      <fieldset disabled={busy} className="mt-4 min-w-0 space-y-4">
        <Field label="Group Name" htmlFor={nameId}>
          <TextField id={nameId} value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
        </Field>
        <AssignmentFields draft={draft} onChange={setDraft} players={players} />
      </fieldset>
      {save.error || remove.error ? <p role="alert" className="mt-2 text-caption text-danger">{describeError(save.error ?? remove.error)}</p> : null}
      <div className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center gap-2 bg-surface py-3">
        {group ? <ConfirmButton label="Delete Group" busy={busy}
          ask={{ title: `Delete ${group.name}?`, detail: 'This removes the saved roster. Existing assignments keep their settings.', confirmLabel: 'Delete Group' }}
          onConfirm={() => remove.mutate(group.id, { onSuccess: () => onDelete(group.id) })} /> : null}
        <Button size="sm" className="ml-auto" variant="ghost" onClick={onClose} disabled={busy}>Back</Button>
        <Button size="sm" variant="primary" busy={save.isPending} disabled={!canSave || busy} onClick={() => {
          save.mutate({ id: group?.id, input: { name: name.trim(), players: ids.map((playerId) => ({ playerId })) } }, { onSuccess: onClose })
        }}>Save Group</Button>
      </div>
    </>
  )
}

function AssignmentFields({ draft, players, currency, onChange }: {
  draft: AssignmentDraft; players: AssignmentPlayer[]; currency?: string; onChange: (draft: AssignmentDraft) => void
}) {
  const id = useId()
  const [search, setSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const searchButton = useRef<HTMLButtonElement>(null)
  const [editing, setEditing] = useState('')
  const selected = Object.keys(draft.players)
  const available = new Map(players.map((player) => [player.id, player]))
  const matching = [...players].filter((player) => `${names(player)} ${player.email}`.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => Number(b.availability === 'active') - Number(a.availability === 'active') || names(a).localeCompare(names(b)))
  const toggle = (playerId: string, checked: boolean) => {
    const next = { ...draft.players }
    if (checked) next[playerId] = {}
    else delete next[playerId]
    onChange({ ...draft, players: next })
  }
  const closeSearch = () => {
    setSearch('')
    setSearching(false)
    searchButton.current?.focus()
  }

  return (
    <div className="space-y-4">
      <section aria-label="Players">
        <div className="flex min-h-8 items-center gap-2">
          <h3 className="text-micro font-medium uppercase tracking-wide text-muted-foreground">Players</h3>
          <div className="ml-auto flex min-w-0 items-center gap-1">
            <AnimatePresence initial={false}>
              {searching ? <motion.div {...FADE} className="min-w-0">
                <TextField id={`${id}-search`} autoFocus data-wf-popover aria-label="Find players" placeholder="Search players"
                  className="h-8 w-44 max-w-full rounded-full bg-tint text-caption"
                  value={search} onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Escape') return
                    event.preventDefault()
                    closeSearch()
                  }} />
              </motion.div> : null}
            </AnimatePresence>
            <IconButton ref={searchButton} size="sm" label={searching ? 'Clear player search' : 'Search players'}
              aria-expanded={searching} aria-controls={searching ? `${id}-search` : undefined}
              onClick={() => searching ? closeSearch() : setSearching(true)}>
              {searching ? <Icons.close /> : <Icons.search />}
            </IconButton>
          </div>
        </div>
        <div className="max-h-[var(--roster-height)] overflow-auto overscroll-contain [--roster-height:11rem] sm:[--roster-height:13rem]" aria-label="Available players" role="group"
          style={searching ? { minHeight: `min(${players.length * 41}px, var(--roster-height))` } : undefined}>
          {matching.map((player) => {
            const values = draft.players[player.id]
            const expanded = values !== undefined && editing === player.id
            return (
              <div key={player.id} className="mb-0.5">
                <div className={cn('flex min-h-10 items-center gap-2 rounded-lg px-1 transition-colors duration-fast hover:bg-tint', values && 'bg-tint/60')}>
                  <Checkbox className="min-w-0 flex-1 items-center gap-2 py-1.5 [&>span:first-of-type]:mt-0 [&>span:first-of-type]:size-4"
                    checked={values !== undefined} onChange={(checked) => toggle(player.id, checked)}>
                    <span className="block truncate">{names(player)}</span>
                  </Checkbox>
                  {currency && values ? <IconButton size="sm" label={`Edit overrides for ${names(player)}`} active={expanded} aria-expanded={expanded}
                    className={cn(Object.keys(values).length && 'text-foreground')}
                    onClick={() => setEditing(expanded ? '' : player.id)}><Icons.edit size="sm" /></IconButton> : null}
                  <span className="flex w-[5.8rem] shrink-0 items-center gap-1.5 pr-1 text-[11px] text-muted-foreground">
                    <span aria-hidden className={cn('size-1.5 rounded-full', player.availability === 'active' ? 'bg-status-green' : 'bg-muted-foreground/40')} />
                    {player.availability === 'active' ? 'Clocked In' : 'Clocked Out'}
                  </span>
                </div>
                {currency && expanded ? (
                  <div className="px-2 pb-3 pt-2 sm:pl-7">
                    <p className="mb-2 text-caption text-muted-foreground">Overrides for {names(player)}</p>
                    <ValuesFields id={`${id}-override`} currency={currency} values={values} overrides
                      onChange={(next) => onChange({ ...draft, players: { ...draft.players, [player.id]: next } })} />
                  </div>
                ) : null}
              </div>
            )
          })}
          {!matching.length ? (
            <p className="py-3 text-caption text-muted-foreground">{search.trim() ? 'No players match your search.' : 'No eligible players available.'}</p>
          ) : null}
        </div>
        {selected.filter((playerId) => !available.has(playerId)).map((playerId) => (
          <div key={playerId} className="flex items-center justify-between gap-2 py-1 text-caption text-muted-foreground">
            <span>Unavailable player ({playerId})</span>
            <Button size="xs" variant="ghost" onClick={() => toggle(playerId, false)}>Deselect</Button>
          </div>
        ))}
      </section>
      {currency ? <section className="pt-2" aria-label="Shared Defaults">
        <h3 className="mb-2 text-micro font-medium uppercase tracking-wide text-muted-foreground">Shared Defaults</h3>
        <ValuesFields id={id} currency={currency} values={draft.defaults} onChange={(defaults) => onChange({ ...draft, defaults })} />
      </section> : null}
    </div>
  )
}

function ValuesFields({ id, currency, values, overrides, onChange }: {
  id: string; currency: string; values: Values; overrides?: boolean; onChange: (values: Values) => void
}) {
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
      {fields.map((key) => {
        const fieldId = `${id}-${key}`
        const label = key === 'paymentAmount' ? `${labels[key]} (${currency})` : labels[key]
        const setValue = (value: string) => onChange({ ...values, [key]: value })
        const customized = key in values
        const input = key === 'notes'
          ? <TextArea id={fieldId} className="min-h-0 resize-y rounded-lg" rows={1} maxLength={2000} value={values[key] ?? ''} onChange={(event) => setValue(event.target.value)} />
          : <TextField id={fieldId} className="h-8 rounded-lg" inputMode="decimal" value={values[key] ?? ''} onChange={(event) => setValue(event.target.value)} />
        return (
          <div key={key} className={key === 'notes' ? 'col-span-2' : undefined}>
            {overrides ? (
              <>
                <div className="mb-1 flex min-h-7 flex-wrap items-center justify-between gap-x-2">
                  <label htmlFor={customized ? fieldId : undefined} className="text-caption font-medium">{label}</label>
                  <Button size="xs" variant="ghost" aria-label={`${customized ? 'Use default' : 'Override'}: ${labels[key]}`} onClick={() => {
                    const next = { ...values }
                    if (customized) delete next[key]
                    else next[key] = ''
                    onChange(next)
                  }}>{customized ? 'Use default' : 'Override'}</Button>
                </div>
                {customized ? input : null}
              </>
            ) : (
              <Field label={label} htmlFor={fieldId} className="h-full justify-end gap-1 [&>label]:text-micro sm:[&>label]:text-caption">{input}</Field>
            )}
          </div>
        )
      })}
    </div>
  )
}
